import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { heritageColors } from '../../../../heritageTheme.ts';
import { getIconById } from '../../../NitzavSpotlightWidget/iconOptions';
import { PARNAS_TYPE_LABELS } from '../../parnasConstants';

/**
 * Tiles — תמיד שלושה ריבועים קבועים (יומי / שבועי / חודשי)
 * כל ריבוע מציג את הפרנס הפעיל לאותו סוג, עם גבול צבעוני מובחן לכל ריבוע.
 * כשאין פרנס פעיל — מוצגים placeholders לשם ולהערה.
 * ירושת פונטים ועיצוב מה-ThemeProvider של הלוח.
 */
const DEFAULT_TILE_COLORS = {
  daily:   '#b8860b',  // זהב חם — פרנס היום
  weekly:  '#0d7377',  // ירוק-ים — פרנס השבוע
  monthly: '#1e3a5f',  // נייבי     — פרנס החודש
};

const ALL_TILE_DEFS = [
  { key: 'daily',   label: 'פרנס היום' },
  { key: 'weekly',  label: 'פרנס השבוע' },
  { key: 'monthly', label: 'פרנס החודש' },
];

const DEFAULT_INCLUDED = { daily: true, weekly: true, monthly: true };

const Tiles = ({
  activeSponsors        = [],
  parnasType            = 'maor',
  parnasTypeCustomLabel = '',
  parnasTypeIcon        = 'star',
  currentParshaName     = '',
  todayHebrewLabel      = '',
  title                 = '',
  showTitle             = false,
  // ייחודי לתבנית Tiles:
  tileColors            = DEFAULT_TILE_COLORS,
  placeholderName       = 'השם שלך כאן',
  placeholderNote       = 'לתרומות ניתן לפנות לגבאי בית הכנסת',
  includedSlotTypes     = DEFAULT_INCLUDED,
}) => {
  const TypeIcon  = getIconById(parnasTypeIcon || 'star');
  const typeLabel = parnasType === 'acher' && parnasTypeCustomLabel
    ? parnasTypeCustomLabel
    : (PARNAS_TYPE_LABELS[parnasType] || '');

  const colors   = { ...DEFAULT_TILE_COLORS, ...tileColors };
  const included = { ...DEFAULT_INCLUDED, ...includedSlotTypes };
  const TILE_DEFS = ALL_TILE_DEFS.filter(d => included[d.key]);

  const byType = {
    daily:   activeSponsors.find(s => s.slotType === 'daily')   || null,
    weekly:  activeSponsors.find(s => s.slotType === 'weekly')  || null,
    monthly: activeSponsors.find(s => s.slotType === 'monthly') || null,
  };

  return (
    <Box dir="rtl" sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Header — ימין-מיושר */}
      <Box sx={{
        px: 2, pt: 1.5, pb: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
        gap: 0.75,
      }}>
        <TypeIcon sx={{ fontSize: 18, color: heritageColors.gold }} />
        <Typography variant="h6" sx={{ color: heritageColors.navy, fontWeight: 700 }}>
          {showTitle && title ? title : `פרנסי ${typeLabel}`}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: heritageColors.border }} />

      {/* Three fixed tiles */}
      <Box sx={{ flex: 1, display: 'flex', gap: 1.5, px: 1.5, py: 1.25, overflow: 'hidden' }}>
        {TILE_DEFS.map(({ key, label }) => {
          const sponsor = byType[key];
          const color   = colors[key];
          const isEmpty = !sponsor;

          return (
            <Box
              key={key}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: `2px solid ${color}`,
                borderRadius: '5px',
                px: 2,
                py: 2,
                textAlign: 'center',
                gap: 0.75,
                background: 'linear-gradient(to bottom, #ffffff 0%, #fdf8ee 100%)',
                boxShadow: '0 2px 8px rgba(160,130,70,0.10), 0 1px 3px rgba(0,0,0,0.04)',
                overflow: 'hidden',
              }}
            >
              {/* Tile label */}
              <Typography variant="body2" sx={{
                color:      color,
                fontSize:   '0.75em',
                fontWeight: 600,
                letterSpacing: '0.02em',
              }}>
                {label}
              </Typography>

              {/* Thin divider in tile color */}
              <Box sx={{ width: '40%', height: '1px', bgcolor: color, opacity: 0.3, my: 0.25 }} />

              {/* Sponsor name or placeholder */}
              <Typography variant="h5" sx={{
                color:      isEmpty ? heritageColors.slate : heritageColors.navy,
                fontWeight: 700,
                lineHeight: 1.25,
                opacity:    isEmpty ? 0.45 : 1,
                wordBreak: 'break-word',
              }}>
                {isEmpty ? placeholderName : sponsor.sponsorName}
              </Typography>

              {/* Note or placeholder note */}
              <Typography variant="body2" sx={{
                color:      heritageColors.slate,
                fontStyle:  'italic',
                fontSize:   '0.75em',
                mt:         0.25,
                opacity:    isEmpty ? 0.4 : 0.85,
                lineHeight: 1.3,
                wordBreak:  'break-word',
              }}>
                {isEmpty ? placeholderNote : (sponsor.note || '')}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Footer */}
      {(currentParshaName || todayHebrewLabel) && (
        <>
          <Divider sx={{ borderColor: heritageColors.border }} />
          <Box sx={{
            px: 2, py: 0.6,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            {currentParshaName ? (
              <Typography variant="body2" sx={{ color: heritageColors.slate, fontSize: '0.8em' }}>
                פרשת {currentParshaName}
              </Typography>
            ) : <Box />}
            {todayHebrewLabel && (
              <Typography variant="body2" sx={{ color: heritageColors.slate, fontSize: '0.8em' }}>
                {todayHebrewLabel}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default Tiles;
